import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StepIndicator from './components/StepIndicator';
import BasicInfoStep from './components/BasicInfoStep';
import BudgetStep from './components/BudgetStep';
import LocationStep from './components/LocationStep';
import RequirementsStep from './components/RequirementsStep';
import JobPreview from './components/JobPreview';
import { jobsService } from '../../services/supabaseService';

const PostJob = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    timeline: '',
    budgetType: 'hourly',
    hourlyRateMin: '',
    hourlyRateMax: '',
    fixedPrice: '',
    paymentTerms: '',
    estimatedHours: '',
    negotiable: false,
    workLocationType: 'on-site',
    city: '',
    address: '',
    radius: '10',
    landmark: '',
    verificationLevel: 'basic',
    requiredSkills: [],
    minExperience: '',
    specialInstructions: '',
    images: [],
    agreeToTerms: false,
  });

  const steps = [
    { number: 1, label: 'Basic Info', component: BasicInfoStep },
    { number: 2, label: 'Budget', component: BudgetStep },
    { number: 3, label: 'Location', component: LocationStep },
    { number: 4, label: 'Requirements', component: RequirementsStep },
    { number: 5, label: 'Preview', component: JobPreview },
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.title || formData?.title?.trim()?.length < 10) {
        newErrors.title = 'Job title must be at least 10 characters';
      }
      if (!formData?.category) {
        newErrors.category = 'Please select a service category';
      }
      if (!formData?.description || formData?.description?.trim()?.length < 50) {
        newErrors.description = 'Description must be at least 50 characters';
      }
      if (!formData?.timeline) {
        newErrors.timeline = 'Please select a timeline';
      }
    }

    if (step === 2) {
      if (formData?.budgetType === 'hourly') {
        if (!formData?.hourlyRateMin || parseFloat(formData?.hourlyRateMin) < 100) {
          newErrors.hourlyRateMin = 'Minimum rate must be at least ₱100';
        }
        if (!formData?.hourlyRateMax || parseFloat(formData?.hourlyRateMax) < 100) {
          newErrors.hourlyRateMax = 'Maximum rate must be at least ₱100';
        }
        if (parseFloat(formData?.hourlyRateMax) < parseFloat(formData?.hourlyRateMin)) {
          newErrors.hourlyRateMax = 'Maximum rate must be greater than minimum';
        }
      } else {
        if (!formData?.fixedPrice || parseFloat(formData?.fixedPrice) < 500) {
          newErrors.fixedPrice = 'Fixed price must be at least ₱500';
        }
      }
      if (!formData?.paymentTerms) {
        newErrors.paymentTerms = 'Please select payment terms';
      }
    }

    if (step === 3) {
      if (!formData?.workLocationType) {
        newErrors.workLocationType = 'Please select work location type';
      }
      if (formData?.workLocationType !== 'remote') {
        if (!formData?.city) {
          newErrors.city = 'Please select a city';
        }
        if (!formData?.address || formData?.address?.trim()?.length < 10) {
          newErrors.address = 'Please provide a detailed address';
        }
      }
    }

    if (step === 4) {
      if (!formData?.verificationLevel) {
        newErrors.verificationLevel = 'Please select verification level';
      }
      if (!formData?.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the terms of service';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const jobData = {
        title: formData?.title,
        category: formData?.category,
        description: formData?.description,
        budgetType: formData?.budgetType,
        budgetMin: formData?.budgetType === 'hourly' ? parseFloat(formData?.hourlyRateMin) : null,
        budgetMax: formData?.budgetType === 'hourly' ? parseFloat(formData?.hourlyRateMax) : null,
        paymentTerms: formData?.paymentTerms,
        estimatedHours: formData?.estimatedHours ? parseInt(formData?.estimatedHours) : null,
        negotiable: formData?.negotiable,
        workLocationType: formData?.workLocationType,
        city: formData?.city,
        address: formData?.address,
        radius: formData?.radius,
        landmark: formData?.landmark,
        verificationLevel: formData?.verificationLevel,
        requiredSkills: formData?.requiredSkills,
        minExperience: formData?.minExperience ? parseInt(formData?.minExperience) : 0,
        specialInstructions: formData?.specialInstructions,
        urgency: 'normal'
      };

      if (formData?.budgetType === 'fixed') {
        jobData.budgetMin = parseFloat(formData?.fixedPrice);
        jobData.budgetMax = parseFloat(formData?.fixedPrice);
      }

      const result = await jobsService?.createJob(jobData);
      
      if (result) {
        setShowSuccessModal(true);
      } else {
        setSubmitError('Failed to create job. Please try again.');
      }
    } catch (error) {
      console.error('Job creation error:', error);
      setSubmitError(error?.message || 'An error occurred while creating the job.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/job-board');
  };

  const CurrentStepComponent = steps?.[currentStep - 1]?.component;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-[60px] lg:pt-[60px]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10 lg:py-12">
          <div className="mb-8">
            <button
              onClick={() => navigate('/home-landing')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm font-medium">Back to Home</span>
            </button>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-3">
              Post a New Job
            </h1>
            <p className="text-base text-gray-600">
              Fill in the details to find the perfect worker for your needs
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
            <StepIndicator 
              currentStep={currentStep} 
              totalSteps={steps?.length}
              steps={steps}
            />
          </div>

          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 md:p-8">
            <CurrentStepComponent
              formData={formData}
              onChange={handleChange}
              errors={errors}
            />

            <div className="flex flex-col-reverse md:flex-row gap-4 mt-8 pt-8 border-t border-gray-200">
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  fullWidth
                  className="md:w-auto"
                >
                  Back
                </Button>
              )}
              
              {currentStep < steps?.length ? (
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                  fullWidth
                  className="md:flex-1"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  iconName="Send"
                  iconPosition="right"
                  fullWidth
                  className="md:flex-1"
                >
                  {isSubmitting ? 'Publishing Job...' : 'Publish Job'}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-8 p-5 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-1" />
              <div className="text-sm text-gray-700">
                <p className="font-medium text-gray-900 mb-2">Need Help?</p>
                <p>
                  Your job will be visible to verified workers in your area. You'll receive applications within 24 hours on average.
                  You can edit or close your job posting anytime from your dashboard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSuccessModal && (
        <div className="fixed inset-0 z-modal bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8 animate-in fade-in zoom-in-95 duration-250">
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <Icon name="CheckCircle" size={40} className="text-green-600" />
              </div>
              
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-3">
                Job Posted Successfully!
              </h2>
              
              <p className="text-base text-gray-600 mb-8">
                Your job has been published and is now visible to qualified workers in your area. You'll start receiving applications soon.
              </p>

              <div className="w-full space-y-4">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleSuccessClose}
                  iconName="Briefcase"
                  iconPosition="left"
                  fullWidth
                >
                  View All Jobs
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/user-dashboard')}
                  iconName="LayoutDashboard"
                  iconPosition="left"
                  fullWidth
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostJob;